import type { UnsubscribeListener } from '@reduxjs/toolkit';
import { freeze } from '@reduxjs/toolkit';
import { useCustomerConfigurations, useCustomers } from '@src/hooks/useCustomer';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import { useGetCounterpartiesQuery } from '@src/providers/AppStateProvider/streamingDataSlice';
import {
  BufferedDispatcher,
  EMPTY_ARRAY,
  getTypedEntries,
  MarketAccountStatusEnum,
  ModeEnum,
  useCurrenciesContext,
  useCurrency,
  useDefaultMarketAccounts,
  useMarketAccountsContext,
  useMarketsContext,
  useSecuritiesContext,
  useUnifiedLiquidityContext,
} from '@talos/kyoko';
import { useEffect, useMemo, useRef } from 'react';
import { useDefaultSubAccount, useFeatureFlag } from '../../hooks';
import { useAggregationsQuery } from '../../hooks/useAggregationRequests';
import { usePriceProtection } from '../../hooks/usePriceProtection';
import {
  OrgConfigurationKey,
  useCustomerMarketAccounts,
  useMarketAccountPermissionFilters,
  useOrgConfiguration,
  useStrategies,
  useSubAccounts,
} from '../../providers';
import { useAdjustedSettlementDates } from '../../providers/AdjustedSettlementDatesProvider';
import { useDisplaySettings, useOrderEntry } from '../../providers/AppConfigProvider';
import type { AppState } from '../../providers/AppStateProvider/types';
import { addAppListener } from '../../providers/AppStateProvider/types';
import { useBaskets } from '../../providers/BasketsProvider';
import { useDefaultAggregation } from '../../providers/DefaultAggregationContext';
import { useFixingIndices } from '../../providers/FixingIndexProvider';
import { useFundDefinitions } from '../../providers/FundDefinitionProvider';
import { useOrderPresets } from '../../providers/OrderPresetsContext';
import { useTradingSettings } from '../../providers/TradingSettingsContext';
import { mapUserAggregationsToMarketSelectorPresets } from '../MarketSelector/presets/mapUserAggregationsToMarketSelectorPresets';
import { multilegComboSliceActions } from '../MultilegCombo/MultilegComboSlice';
import { setReferenceData as setCareOrderReferenceData } from './CareOrderFormView/CareOrderSlice';
import { setReferenceData as setManualDealReferenceData } from './ManualDealTradeView/ManualDealSlice';
import { orderSliceActions } from './NewOrder/OrderSlice';
import { rfqSliceActions } from './NewRFQ/RFQSlice';
import { setRealTimeOMSSettings } from './OMSRealTimeSettingsSlice';
import {
  setAdjustedSettlementDates,
  setBaskets,
  setCounterparties,
  setCurrencies,
  setCustomerConfigurations,
  setCustomerMarketAccounts,
  setCustomers,
  setDefaultSubAccount,
  setFixingIndices,
  setFunds,
  setMarketAccountPermissionFilters,
  setMarketAccounts,
  setMarkets,
  setOMSSettings,
  setOptions,
  setOrderPresets,
  setSecurities,
  setStrategies,
  setTradingAggregationPresets,
  setUnifiedLiquidityTokens,
} from './OMSReferenceDataSlice';
import { loadOmsPopoutGroups } from './OMSSlice';
import { setReferenceData as setOrderPresetsReferenceData } from './Presets/OrderPresetsSlice';
import { setReferenceData as setSalesOrderReferenceData } from './SalesOrder/SalesOrderSlice';
import { setReferenceData as setSalesRFQReferenceData } from './SalesRFQView/SalesRFQSlice';
import { MARKET_SELECTION_ASYNC_REFDATA_TIMEOUT_MS, type OMSPopoutGroup, type OMSPopoutGroupType } from './types';

export const useOMSDependencies = (): void => {
  const dispatch = useAppStateDispatch();
  const { setOmsPopoutGroups, omsPopoutGroups } = useTradingSettings();

  const dispatcher = useMemo(() => new BufferedDispatcher(dispatch), [dispatch]);

  // This is a bit shady but I want this Query to load instantly for all things OMS-related for better UX.
  // Invoking this hook here will fetch the data (and cache it for the actual users of the data)
  const { data: aggregationsQueryData } = useAggregationsQuery();

  const tradingAggregationPresets = useMemo(
    () => mapUserAggregationsToMarketSelectorPresets(aggregationsQueryData),
    [aggregationsQueryData]
  );

  useEffect(() => {
    dispatcher.dispatchImmediate(setTradingAggregationPresets(tradingAggregationPresets));
  }, [dispatcher, tradingAggregationPresets]);

  const { defaultAggregationId } = useDefaultAggregation();

  const {
    securitiesBySymbol,
    securitiesList,
    perps,
    cfds,
    perpsByCurrencyPair,
    futures,
    futuresByCurrencyPair,
    futuresByMarketByCurrency,
  } = useSecuritiesContext();

  const { defaultSubAccount } = useDefaultSubAccount();
  useEffect(() => {
    dispatcher.dispatchImmediate(setDefaultSubAccount(defaultSubAccount?.Name));
  }, [dispatcher, defaultSubAccount]);

  useEffect(() => {
    dispatcher.dispatchImmediate(
      setSecurities({
        securitiesList: securitiesList,
        securitiesBySymbol,
        perps,
        cfds,
        perpsByCurrencyPair,
        futures,
        futuresByCurrencyPair,
        futuresByMarketByCurrency,
        isReady: true,
      })
    );
  }, [
    dispatcher,
    securitiesList,
    securitiesBySymbol,
    perps,
    cfds,
    perpsByCurrencyPair,
    futures,
    futuresByCurrencyPair,
    futuresByMarketByCurrency,
  ]);

  const { data: counterparties } = useGetCounterpartiesQuery({});
  useEffect(() => {
    if (counterparties != null || dispatcher.isTypeInitialised(setCounterparties.type)) {
      const counterpartiesArray = Array.from(counterparties?.values() ?? []);
      dispatcher.dispatchImmediate(setCounterparties(counterpartiesArray));
    }
  }, [dispatcher, counterparties]);

  const availableCustomers = useCustomers();
  const enabledCustomers = useMemo(
    // When the Pricing Rule doesn't exist for a customer, PricingRuleMode returns as an empty string ''
    // This happens for clients using Customer Tiering and Configurations
    // Only filter out explicitly PricingRuleMode === 'Disabled' records.
    () => availableCustomers?.filter(({ PricingRuleMode }) => PricingRuleMode !== ModeEnum.Disabled),
    [availableCustomers]
  );

  useEffect(() => {
    if (enabledCustomers != null || dispatcher.isTypeInitialised(setCustomers.type)) {
      dispatcher.dispatch(setCustomers(enabledCustomers!));
    }
  }, [dispatcher, enabledCustomers]);

  const { customerMarketAccountsList } = useCustomerMarketAccounts();
  useEffect(() => {
    if (customerMarketAccountsList != null || dispatcher.isTypeInitialised(setCustomerMarketAccounts.type)) {
      dispatcher.dispatch(
        setCustomerMarketAccounts(
          customerMarketAccountsList!.filter(ma => ma.Status === MarketAccountStatusEnum.Active)
        )
      );
    }
  }, [dispatcher, customerMarketAccountsList]);

  const customerConfigurations = useCustomerConfigurations();
  useEffect(() => {
    if (customerConfigurations != null || dispatcher.isTypeInitialised(setCustomerConfigurations.type)) {
      dispatcher.dispatch(setCustomerConfigurations(customerConfigurations ?? []));
    }
  }, [dispatcher, customerConfigurations]);

  const { marketAccountsList, marketAccountsByMarket, marketAccountsByName } = useMarketAccountsContext();
  useEffect(() => {
    if (marketAccountsList != null || dispatcher.isTypeInitialised(setMarketAccounts.type)) {
      dispatcher.dispatch(
        setMarketAccounts({ marketAccountsList, marketAccountsByMarket, marketAccountsByName, isReady: true })
      );
    }
  }, [dispatcher, marketAccountsList, marketAccountsByMarket, marketAccountsByName]);

  const { strategiesList, strategiesByName, ddhStrategiesList } = useStrategies();
  useEffect(() => {
    if (strategiesList != null || dispatcher.isTypeInitialised(setStrategies.type)) {
      dispatcher.dispatchImmediate(
        setStrategies({ strategiesList, strategiesByName, ddhStrategiesList, isReady: true })
      );
    }
  }, [dispatcher, strategiesList, strategiesByName, ddhStrategiesList]);

  const { marketsList, marketsByName } = useMarketsContext();
  useEffect(() => {
    if (marketsList != null || dispatcher.isTypeInitialised(setMarkets.type)) {
      dispatcher.dispatch(setMarkets({ marketsList, marketsByName, isReady: true }));
    }
  }, [dispatcher, marketsList, marketsByName]);

  const { options, expirationByMarketByCurrencyIdentity, optionSecurityBySymbol, strikesByOptionKey } =
    useSecuritiesContext();
  useEffect(() => {
    if (options != null || dispatcher.isTypeInitialised(setOptions.type)) {
      dispatcher.dispatch(
        setOptions({
          options,
          expirationByMarketByCurrencyIdentity,
          optionSecurityBySymbol,
          strikesByOptionKey,
          isReady: true,
        })
      );
    }
  }, [dispatcher, options, expirationByMarketByCurrencyIdentity, optionSecurityBySymbol, strikesByOptionKey]);

  const { currenciesBySymbol } = useCurrenciesContext();
  useEffect(() => {
    if (currenciesBySymbol != null || dispatcher.isTypeInitialised(setCurrencies.type)) {
      dispatcher.dispatch(setCurrencies({ currenciesBySymbol, isReady: true }));
    }
  }, [dispatcher, currenciesBySymbol]);

  const { orderPresetsByID, orderPresetsList } = useOrderPresets();
  useEffect(() => {
    if (orderPresetsList != null || dispatcher.isTypeInitialised(setOrderPresets.type)) {
      dispatcher.dispatch(
        setOrderPresets({
          orderPresetsList: orderPresetsList,
          orderPresetsByID: orderPresetsByID,
          isReady: true,
        })
      );
    }
  }, [dispatcher, orderPresetsList, orderPresetsByID]);

  const { unifiedLiquidityTokenBySymbol } = useUnifiedLiquidityContext();
  useEffect(() => {
    if (unifiedLiquidityTokenBySymbol?.size || dispatcher.isTypeInitialised(setUnifiedLiquidityTokens.type)) {
      unifiedLiquidityTokenBySymbol && dispatcher.dispatch(setUnifiedLiquidityTokens(unifiedLiquidityTokenBySymbol));
    }
  }, [dispatcher, unifiedLiquidityTokenBySymbol]);

  const { tradableSubAccounts } = useSubAccounts();
  // For options that support UnderlyingQuoteCurrency pricing mode (boolean), UI currently needs to hard code USD
  // https://talostrading.atlassian.net/browse/UI-2809
  const USD = useCurrency('USD');
  useEffect(() => {
    dispatch(
      orderSliceActions.setAdditionalDependencies({
        tradableSubAccounts: tradableSubAccounts || EMPTY_ARRAY,
        USD,
      })
    );
  }, [tradableSubAccounts, USD, dispatch]);

  const { fixingIndicesList, fixingIndicesByName, tradableIndicesBySymbol, marketsByFixingIndex, isLoaded } =
    useFixingIndices();
  useEffect(() => {
    if (isLoaded) {
      dispatcher.dispatchImmediate(
        setFixingIndices({
          fixingIndicesList,
          fixingIndicesByName,
          tradableIndicesBySymbol,
          marketsByFixingIndex,
          isReady: true,
        })
      );
    }
  }, [fixingIndicesList, fixingIndicesByName, tradableIndicesBySymbol, marketsByFixingIndex, isLoaded, dispatcher]);

  const { baskets } = useBaskets();

  useEffect(() => {
    dispatcher.dispatchImmediate(setBaskets({ basketsList: baskets ?? [] }));
  }, [baskets, dispatcher]);

  const { fundsBySymbol, fundsList } = useFundDefinitions();

  useEffect(() => {
    dispatcher.dispatchImmediate(setFunds({ fundsBySymbol, fundsList }));
  }, [fundsBySymbol, fundsList, dispatcher]);

  const { settlementDates } = useAdjustedSettlementDates();

  useEffect(() => {
    dispatcher.dispatchImmediate(setAdjustedSettlementDates({ settlementDates }));
  }, [settlementDates, dispatcher]);

  const { marketAccountFilterIDsBySubAccountName, marketAccountPermissionFiltersByFilterID } =
    useMarketAccountPermissionFilters();
  useEffect(() => {
    if (marketAccountFilterIDsBySubAccountName && marketAccountPermissionFiltersByFilterID) {
      dispatcher.dispatchImmediate(
        setMarketAccountPermissionFilters({
          marketAccountFilterIDsBySubAccountName,
          marketAccountPermissionFiltersByFilterID,
          isReady: true,
        })
      );
    }
  }, [marketAccountPermissionFiltersByFilterID, marketAccountFilterIDsBySubAccountName, dispatcher]);

  const {
    useTradeAllocations,
    enableGroups,
    enableDerivativeContractDefault,
    defaultNewOrderToCounterCurrency,
    alwaysCheckPriceReasonability,
    defaultOrderStrategy,
    defaultMultilegOrderStrategy,
    allowSyntheticCcy,
    allowSyntheticCrosses,
    exclusivelyPrimePriceOnRePriming,
    enableReduceFirst,
    enableReduceOnly,
    defaultToEmptyOrderMarkets,
    resetOrderAccountsOnSubAccountChange,
    useNewMarketSelector,
    rfqFormTradingProtocol,
    rfqFormDefaultToSweeping,
    rfqFormDefaultToSizeCountering,
  } = useTradingSettings();
  const { lastProductGroupUsed, lastStrategyUsed, ddhDefaultsByID } = useOrderEntry();
  const { showAllInPrices, showDelta1BPS } = useDisplaySettings();
  const { defaultPriceProtection } = usePriceProtection();
  const {
    supportedStrategiesForPricingModes,
    enableOptionTrading,
    enableNegotiatedRFQFlow,
    enableTakerMarketplaceETFRFQFlow,
    careOrderBlockDirectExecution,
    truncateCareOrderID,
    enableAccountSegregation,
    enableNewMarketSelectorMSSValidation,
    disableNonSTFNegotiatedRFQ,
    showStFMarketsInNonStFNegotiatedRFQ,
    disableUnifiedLiquidityDerivativesMarketSelectionRestriction,
    enableUnifiedLiquidityMarketSymbols,
    disableCrossCurrencyQty,
    nonSpotDelta1DefaultQty,
    enableRFQFormOptions,
    enableRFQFormOTCs,
    enableSTSDigitalCustomOptionRFQ,
    enableRFQFormFundSecurities,
    enablePredictionMarkets,
    enableRFQSweeping,
    enableRFQSizeCounteringMultitokenCompound,
    enableRFQSizeCounteringSpot,
    rfqFormClearingAccountProductTypes,
    sizeCounteringImpliesPartialFill,
    fundCareOrderDefaultSubAccounts,
    useFundDefinitionAndTradableIndexesStreams,
    supportAdjustedSettlementDate,
    defaultBasketSettleOffsetOverride,
    enableRFQFormFundField,
    enableDurationSubmission,
    enableCareOrderOrders,
  } = useFeatureFlag();
  const { getConfig } = useOrgConfiguration();
  const marketSelectionAsyncRefDataTimeoutMs = getConfig(
    OrgConfigurationKey.OrderFormMarketSelectionAsyncRefDataTimeoutMs,
    MARKET_SELECTION_ASYNC_REFDATA_TIMEOUT_MS
  );
  const { defaultMarketAccounts } = useDefaultMarketAccounts();

  useEffect(() => {
    dispatcher.dispatch(
      setOMSSettings({
        useTradeAllocations,
        enableGroups,
        enableDerivativeContractDefault,
        defaultNewOrderToCounterCurrency,
        alwaysCheckPriceReasonability,
        defaultOrderStrategy,
        defaultMultilegOrderStrategy,
        lastProductGroupUsed,
        lastStrategyUsed,
        showAllInPrices,
        showDelta1BPS,
        defaultPriceProtection,
        supportedStrategiesForPricingModes,
        enableRFQFormFundSecurities,
        enableRFQFormOptions,
        enableRFQFormOTCs,
        enableSTSDigitalCustomOptionRFQ,
        enablePredictionMarkets,
        enableRFQSweeping,
        enableRFQSizeCounteringMultitokenCompound,
        enableRFQSizeCounteringSpot,
        rfqFormClearingAccountProductTypes,
        sizeCounteringImpliesPartialFill,
        enableOptionTrading,
        enableNegotiatedRFQFlow,
        allowSyntheticCcy,
        allowSyntheticCrosses,
        exclusivelyPrimePriceOnRePriming,
        enableReduceFirst,
        enableReduceOnly,
        ddhDefaultsByID,
        enableTakerMarketplaceETFRFQFlow,
        enableCareOrderOrders,
        careOrderBlockDirectExecution,
        truncateCareOrderID,
        defaultToEmptyOrderMarkets,
        defaultMarketAccounts,
        defaultAggregationId,
        resetOrderAccountsOnSubAccountChange,
        enableAccountSegregation,
        enableMSSValidation: useNewMarketSelector && enableNewMarketSelectorMSSValidation,
        marketSelectionAsyncRefDataTimeoutMs,
        disableNonSTFNegotiatedRFQ,
        showStFMarketsInNonStFNegotiatedRFQ,
        disableUnifiedLiquidityDerivativesMarketSelectionRestriction,
        enableUnifiedLiquidityMarketSymbols,
        disableCrossCurrencyQty,
        nonSpotDelta1DefaultQty,
        fundCareOrderDefaultSubAccounts,
        useFundDefinitionAndTradableIndexesStreams,
        supportAdjustedSettlementDate,
        defaultBasketSettleOffsetOverride,
        enableRFQFormFundField,
        enableDurationSubmission,
        rfqFormTradingProtocol,
        rfqFormDefaultToSweeping,
        rfqFormDefaultToSizeCountering,
      })
    );
  }, [
    dispatcher,
    useTradeAllocations,
    enableGroups,
    enableDerivativeContractDefault,
    defaultNewOrderToCounterCurrency,
    alwaysCheckPriceReasonability,
    defaultOrderStrategy,
    defaultMultilegOrderStrategy,
    lastProductGroupUsed,
    lastStrategyUsed,
    showAllInPrices,
    showDelta1BPS,
    defaultPriceProtection,
    supportedStrategiesForPricingModes,
    enableRFQFormFundSecurities,
    enableRFQFormOptions,
    enableRFQFormOTCs,
    enableSTSDigitalCustomOptionRFQ,
    enablePredictionMarkets,
    enableRFQSweeping,
    enableRFQSizeCounteringMultitokenCompound,
    enableRFQSizeCounteringSpot,
    rfqFormClearingAccountProductTypes,
    sizeCounteringImpliesPartialFill,
    enableOptionTrading,
    enableNegotiatedRFQFlow,
    allowSyntheticCcy,
    allowSyntheticCrosses,
    exclusivelyPrimePriceOnRePriming,
    enableReduceFirst,
    enableReduceOnly,
    ddhDefaultsByID,
    enableTakerMarketplaceETFRFQFlow,
    enableCareOrderOrders,
    careOrderBlockDirectExecution,
    truncateCareOrderID,
    defaultToEmptyOrderMarkets,
    defaultMarketAccounts,
    defaultAggregationId,
    resetOrderAccountsOnSubAccountChange,
    enableAccountSegregation,
    useNewMarketSelector,
    enableNewMarketSelectorMSSValidation,
    marketSelectionAsyncRefDataTimeoutMs,
    disableNonSTFNegotiatedRFQ,
    showStFMarketsInNonStFNegotiatedRFQ,
    disableUnifiedLiquidityDerivativesMarketSelectionRestriction,
    enableUnifiedLiquidityMarketSymbols,
    disableCrossCurrencyQty,
    nonSpotDelta1DefaultQty,
    fundCareOrderDefaultSubAccounts,
    useFundDefinitionAndTradableIndexesStreams,
    supportAdjustedSettlementDate,
    defaultBasketSettleOffsetOverride,
    enableRFQFormFundField,
    enableDurationSubmission,
    rfqFormTradingProtocol,
    rfqFormDefaultToSweeping,
    rfqFormDefaultToSizeCountering,
  ]);

  // Real-time settings: dispatch immediately (not through BufferedDispatcher) to avoid stale data
  useEffect(() => {
    dispatch(
      setRealTimeOMSSettings({
        showDelta1BPS,
      })
    );
  }, [dispatch, showDelta1BPS]);

  // Sharing references
  const omsReferenceDataState = useAppStateSelector(state => state.referenceData);
  useEffect(() => {
    // We need to freeze this state because the ref data objects are so large.
    // If we don't freeze them, we will get a performance hit as immer will iterate into the entire object to look for any changes in the draft.
    const frozenState = freeze(omsReferenceDataState);
    dispatch(setSalesOrderReferenceData(frozenState));
    dispatch(orderSliceActions.setReferenceData(frozenState));
    dispatch(setSalesRFQReferenceData(frozenState));
    dispatch(rfqSliceActions.setReferenceData(frozenState));
    dispatch(multilegComboSliceActions.setReferenceData(frozenState));
    dispatch(setOrderPresetsReferenceData(frozenState));
    dispatch(setCareOrderReferenceData(frozenState));
    dispatch(setManualDealReferenceData(frozenState));
  }, [dispatch, omsReferenceDataState]);

  const isLoadedRef = useRef(false);
  const loadedOmsPopoutGroups = useAppStateSelector(state => state.OMS.popoutGroups);
  useEffect(() => {
    if (omsPopoutGroups == null || isLoadedRef.current === true) {
      return;
    }
    isLoadedRef.current = true;
    dispatch(loadOmsPopoutGroups(omsPopoutGroups));
  }, [dispatch, omsPopoutGroups, loadedOmsPopoutGroups]);

  useEffect(() => {
    // Add listener which updates data whenever the state is updated
    const unsubscribe = dispatch(
      addAppListener({
        predicate: (_, currentState, previousState) => {
          return currentState.OMS.popoutGroups !== previousState.OMS.popoutGroups;
        },
        effect: async (_, listenerApi) => {
          const newState = listenerApi.getState();
          const popoutGroups = selectOMSPopoutsToPersist(newState);
          setOmsPopoutGroups(popoutGroups);
        },
      })
    );

    // Cast below should be fixed once we use `withTypes` more
    // See: https://github.com/reduxjs/redux-toolkit/discussions/2798#discussioncomment-3917583
    return unsubscribe as unknown as UnsubscribeListener;
  }, [dispatch, setOmsPopoutGroups]);
};

function selectOMSPopoutsToPersist(state: AppState) {
  return getTypedEntries(state.OMS.popoutGroups).reduce(
    (acc, [key, value]) => {
      if (value != null) {
        acc[key] = {
          activeView: value.activeView,
          features: value.features,
          views: value.views,
          name: value.name,
        };
      }
      return acc;
    },
    {} as Partial<Record<OMSPopoutGroupType, Omit<OMSPopoutGroup, '_window'>>>
  );
}
